import { Serializer } from "survey-core";
import { getLocaleStrings } from "survey-creator-core";

export const registerSurveyProperties = () => {
  // --- 1. SET UP HELP TEXTS (PEHELP) ---
  // This maps internal property names to the hint text shown in the UI
  const translations = getLocaleStrings("en");

  // Validation Type
  translations.pehelp.validationType =
    "Select the specific validation logic for this question.";

  // Compare Validation
  translations.pehelp.compareTo =
    "Select a question to compare this answer against.";
  translations.pehelp.compareValue =
    "A static value to check against (e.g. 100).";
  translations.pehelp.compareOperator =
    "The mathematical logic used for the comparison.";
  translations.pehelp.compareError =
    "Message shown to the user when the comparison fails.";

  // Window Validation
  translations.pehelp.windowType =
    "Define if the date must be before, after, or within a range.";
  translations.pehelp.windowBaseQuestion =
    "The date question used as the reference point.";
  translations.pehelp.windowAmount =
    "The number of days allowed for the offset.";
  translations.pehelp.windowError =
    "Message shown when the date falls outside the allowed window.";

  // Value Validation
  translations.pehelp.AcceptedInputs =
    "Specify allowed characters or regex patterns (e.g., [0-9]*).";
  translations.pehelp.valueCustomError =
    "The message displayed when input does not match Accepted Inputs.";

  // Conditional Dependency
  translations.pehelp.dependsOn =
    "Trigger validation based on the answer to this source question.";
  translations.pehelp.dependsValue =
    "The specific value from the source question that triggers this rule.";
  translations.pehelp.dependsError =
    "Error message shown if the dependency condition is not met.";
  translations.pehelp.dependencyAction =
    "The action taken (Show/Enable/Require) when the condition matches.";
  translations.pehelp.dependsSetValue =
    "The specific value to automatically populate in this field.";

  // Range Validations
  translations.pehelp.conditionalRangeShow =
    "Toggle to enable range-based check logic.";
  translations.pehelp.conditionalRangeRules =
    "Define the specific min/max rules for this range check.";
  translations.pehelp.conditionalRangeBaseQuestion =
    "The numeric question used to calculate the range.";
  translations.pehelp.rowRangeShow =
    "Toggle to enable range checks for matrix rows.";
  translations.pehelp.rowRangeRules =
    "Logic rules applied to each individual row in the matrix.";

  // Helper to check if a property has a valid value assigned
  const isFilled = (val: any) =>
    val !== undefined && val !== null && val !== "";

  // 2. THE MAIN TOGGLE (Validation Type)
  Serializer.addProperty("question", {
    name: "validationType",
    category: "validation",
    displayName: "Validation Type",
    type: "radiogroup",
    default: "",
    choices: (obj: any) => {
      const inputType = obj.inputType || obj.getType();
      if (inputType === "text")
        return [
          { value: "", text: "None" },
          { value: "value", text: "Value Validation" },
          { value: "conditional", text: "Conditional Validation" },
        ];
      if (inputType === "date")
        return [
          { value: "", text: "None" },
          { value: "window", text: "Window Validation" },
          { value: "compare", text: "Compare Validation" },
          { value: "conditional", text: "Conditional Validation" },
        ];
      if (inputType === "number")
        return [
          { value: "", text: "None" },
          { value: "compare", text: "Compare Validation" },
          { value: "conditional-range", text: "Conditional Range Check" },
          { value: "conditional", text: "Conditional Validation" },
          { value: "minAndMaxValidation", text: "Min/Max Validation" },
        ];
      if (obj.getType() === "matrixdropdown")
        return [
          { value: "", text: "None" },
          { value: "row-range", text: "Row Range Validation" },
        ];
      return [
        { value: "", text: "None" },
        { value: "conditional", text: "Conditional Validation" },
      ];
    },
  });

  // 3. ADD CUSTOM PROPERTIES
  // Compare Validation
  Serializer.addProperty("question", {
    name: "compareTo",
    category: "validation",
    type: "question",
    displayName: "Compare to",
  });
  Serializer.addProperty("question", {
    name: "compareValue",
    category: "validation",
    type: "string",
    displayName: "Compare value",
  });
  Serializer.addProperty("question", {
    name: "compareOperator",
    category: "validation",
    type: "dropdown",
    displayName: "Compare operator",
    choices: ["less", "greater", "equals", "not_equals"],
  });
  Serializer.addProperty("question", {
    name: "compareError",
    category: "validation",
    type: "string",
    displayName: "Compare error",
  });

  // Window Validation
  Serializer.addProperty("question", {
    name: "windowType",
    category: "validation",
    type: "dropdown",
    choices: ["", "before", "after", "beforeAfter"],
  });
  Serializer.addProperty("question", {
    name: "windowBaseQuestion",
    category: "validation",
    type: "question",
  });
  Serializer.addProperty("question", {
    name: "windowAmount",
    category: "validation",
    type: "number",
  });
  Serializer.addProperty("question", {
    name: "windowError",
    category: "validation",
    type: "string",
  });

  // Value Validation
  Serializer.addProperty("question", {
    name: "AcceptedInputs",
    category: "validation",
    type: "string",
    displayName: "Accepted Inputs",
  });
  Serializer.addProperty("question", {
    name: "valueCustomError",
    category: "validation",
    type: "string",
    displayName: "Custom error message",
  });

  // Conditional Dependency
  Serializer.addProperty("question", {
    name: "dependsOn",
    category: "validation",
    type: "question",
    displayName: "Depends on",
  });
  Serializer.addProperty("question", {
    name: "dependsValue",
    category: "validation",
    type: "string",
    displayName: "Depends value",
  });
  Serializer.addProperty("question", {
    name: "dependsError",
    category: "validation",
    type: "string",
    displayName: "Depends error",
  });
  Serializer.addProperty("question", {
    name: "dependencyAction",
    category: "validation",
    type: "dropdown",
    choices: ["", "show", "enable", "require", "setValue"],
  });
  Serializer.addProperty("question", {
    name: "dependsSetValue",
    category: "validation",
    type: "string",
    displayName: "Set value to",
  });

  // Range Validations
  Serializer.addProperty("question", {
    name: "conditionalRangeShow",
    category: "validation",
    type: "boolean",
    default: false,
    displayName: "Enable Range Check",
  });
  Serializer.addProperty("question", {
    name: "conditionalRangeRules",
    category: "validation",
    type: "text",
    displayName: "Range Rules",
  });
  Serializer.addProperty("question", {
    name: "conditionalRangeBaseQuestion",
    category: "validation",
    type: "question",
    displayName: "Range Base Question",
  });
  Serializer.addProperty("question", {
    name: "rowRangeShow",
    category: "validation",
    type: "boolean",
    default: false,
    displayName: "Enable Row Range",
  });
  Serializer.addProperty("question", {
    name: "rowRangeRules",
    category: "validation",
    type: "text",
    displayName: "Row Range Rules",
  });

  // 7. MIN / MAX VALUE VALIDATION
  Serializer.addProperty("question", {
    name: "customMin",
    category: "validation",
    type: "number",
    displayName: "Minimum Value",
  });

  Serializer.addProperty("question", {
    name: "customMax",
    category: "validation",
    type: "number",
    displayName: "Maximum Value",
  });

  Serializer.addProperty("question", {
    name: "customMinError",
    category: "validation",
    type: "string",
    displayName: "Min Error Message",
  });

  Serializer.addProperty("question", {
    name: "customMaxError",
    category: "validation",
    type: "string",
    displayName: "Max Error Message",
  });

  // 4. APPLY CONDITIONAL RENDERING (visibleIf)
  ["dependsOn"].forEach((propName) => {
    const prop = Serializer.findProperty("question", propName);
    if (prop)
      prop.visibleIf = (q: any) =>
        q.validationType === "conditional" && !q.getType().startsWith("matrix");
  });

  ["dependsValue", "dependsError", "dependencyAction"].forEach((propName) => {
    const prop = Serializer.findProperty("question", propName);
    if (prop)
      prop.visibleIf = (q: any) =>
        q.validationType === "conditional" && isFilled(q.dependsOn);
  });

  const dependsSetValueProp = Serializer.findProperty(
    "question",
    "dependsSetValue",
  );
  if (dependsSetValueProp) {
    dependsSetValueProp.visibleIf = (q: any) =>
      q.validationType === "conditional" &&
      isFilled(q.dependsOn) &&
      q.dependencyAction === "setValue";
  }

  const allowedCompareTypes = [
    "date",
    "time",
    "datetime-local",
    "datetime",
    "password",
    "number",
  ];
  Serializer.findProperty("question", "compareTo").visibleIf = (q: any) =>
    allowedCompareTypes.includes(q.inputType || q.getType()) &&
    q.validationType === "compare" &&
    !isFilled(q.compareValue);
  Serializer.findProperty("question", "compareValue").visibleIf = (q: any) =>
    allowedCompareTypes.includes(q.inputType || q.getType()) &&
    q.validationType === "compare" &&
    !isFilled(q.compareTo);
  Serializer.findProperty("question", "compareOperator").visibleIf = (q: any) =>
    q.validationType === "compare" &&
    (isFilled(q.compareTo) || isFilled(q.compareValue));
  Serializer.findProperty("question", "compareError").visibleIf = (q: any) =>
    q.validationType === "compare" &&
    (isFilled(q.compareTo) || isFilled(q.compareValue));

  ["windowType", "windowBaseQuestion", "windowAmount", "windowError"].forEach(
    (propName) => {
      const prop = Serializer.findProperty("question", propName);
      if (prop)
        prop.visibleIf = (q: any) =>
          q.inputType === "date" && q.validationType === "window";
    },
  );
  ["customMin", "customMax", "customMinError", "customMaxError"].forEach(
    (propName) => {
      const prop = Serializer.findProperty("question", propName);
      if (prop) {
        prop.visibleIf = (q: any) =>
          q.inputType === "number" &&
          q.validationType === "minAndMaxValidation";
      }
    },
  );

  ["AcceptedInputs", "valueCustomError"].forEach((propName) => {
    const prop = Serializer.findProperty("question", propName);
    if (prop)
      prop.visibleIf = (q: any) =>
        q.inputType === "text" && q.validationType === "value";
  });

  Serializer.findProperty("question", "conditionalRangeRules").visibleIf = (
    q: any,
  ) => q.conditionalRangeShow === true;
  Serializer.findProperty("question", "rowRangeRules").visibleIf = (q: any) =>
    q.rowRangeShow === true;

  // 5. CLEANUP & REMOVAL
  Serializer.getProperties("question").forEach((prop) => {
    if (
      prop.displayName === "Must contain text" ||
      prop.name === "text" ||
      prop.name === "valueRegex" ||
      prop.name === "valuecontains"
    ) {
      prop.visible = false;
    }
  });

  // 6. REORDERING
  const acceptedInputsProp = Serializer.findProperty(
    "question",
    "AcceptedInputs",
  );
  if (acceptedInputsProp) acceptedInputsProp.visibleIndex = 10;
  const customErrorProp = Serializer.findProperty(
    "question",
    "valueCustomError",
  );
  if (customErrorProp) customErrorProp.visibleIndex = 20;

  const inputTypeProperty = Serializer.findProperty("text", "inputType");
  if (inputTypeProperty) {
    inputTypeProperty.setChoices([
      "date",
      "datetime-local",
      "email",
      "number",
      "password",
      "text",
      "time",
    ]);
  }
  const conditionalRangeShowProp = Serializer.findProperty(
    "question",
    "conditionalRangeShow",
  );
  if (conditionalRangeShowProp) {
    conditionalRangeShowProp.visibleIf = (q: any) =>
      q.validationType === "conditional-range" &&
      (q.inputType === "number" || q.getType() === "text");
  }

  const conditionalRangeBaseProp = Serializer.findProperty(
    "question",
    "conditionalRangeBaseQuestion",
  );
  if (conditionalRangeBaseProp) {
    conditionalRangeBaseProp.visibleIf = (q: any) =>
      q.validationType === "conditional-range" &&
      q.conditionalRangeShow === true;
  }

  const rowRangeShowProp = Serializer.findProperty("question", "rowRangeShow");
  if (rowRangeShowProp) {
    rowRangeShowProp.visibleIf = (q: any) =>
      q.validationType === "row-range" && q.getType() === "matrixdropdown";
  }
};
